import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Box, AlertTriangle, ArrowRight, IndianRupee } from 'lucide-react';
import { API_URL } from '../utils/cartApi';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ totalSales: 0, orderCount: 0, outOfStock: 0 });
  const [recentProducts, setRecentProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetching from your API
      const [prodRes, orderRes] = await Promise.all([
        axios.get(`${API_URL}/api/products/all`),
        axios.get(`${API_URL}/api/orders/all`)
      ]);

      const allProds = Array.isArray(prodRes.data)
        ? prodRes.data
        : prodRes.data.products || [];

      const allOrders = Array.isArray(orderRes.data)
        ? orderRes.data
        : orderRes.data.orders || [];

      // Aggressive filter that catches EVERYTHING (0, negatives, empty strings, nulls, and NaNs)
      const outOfStockItems = allProds.filter(product => {
        if (product.isDeleted) return false;

        const stockVal = product.stock;
        
        return (
            !stockVal || 
            stockVal === 0 || 
            stockVal === "0" || 
            Number(stockVal) <= 0 || 
            isNaN(Number(stockVal)) // Catches text strings like "N/A"
        );
      });

      // 🛑 DEBUGGING LOGS - Check your browser console after saving!
      console.log("🛠️ DEBUG - Total Products Fetched:", allProds.length);
      console.log("🛠️ DEBUG - Out of Stock Items Found:", outOfStockItems.length);

      const totalRevenue = allOrders.reduce((acc, curr) => {
        return acc + (Number(curr.totalAmount || curr.total || curr.billing?.total) || 0);
      }, 0);

      setStats({
        totalSales: totalRevenue,
        orderCount: allOrders.length,
        outOfStock: outOfStockItems.length // Dynamically uses the aggressive filter
      });

      setRecentProducts(allProds.slice(0, 4));
      setRecentOrders(allOrders.slice(0, 3));
    } catch (err) {
      console.error("Error loading dashboard data", err);
    }
  };

  return (
    <div className="p-8 bg-[#f5f2e8] min-h-screen relative overflow-hidden">
      
      {/* 1. BRANDING SECTION (LOGO ONLY) */}
      <div className="flex flex-col items-center justify-center mb-10 relative z-10">
        <div className="w-48 h-48 flex items-center justify-center drop-shadow-md">
          <img 
            src="/logo.png" 
            alt="Sri Dorasani Collections" 
            className="max-w-full max-h-full object-contain" 
          />
        </div>
      </div>

      {/* 2. DASHBOARD TITLE */}
      <h2 className="text-xl font-bold text-maroon mb-8 uppercase font-serif relative z-10 tracking-wider">
        Dashboard Overview
      </h2>

      {/* 3. TOP METRIC CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 relative z-10">
        <StatCard 
          icon={<ShoppingBag className="text-maroon" size={20} />} 
          label="Total Orders" 
          value={stats.orderCount} 
        />
        <StatCard 
          icon={<IndianRupee className="text-maroon" size={20} />} 
          label="Total Sales" 
          value={stats.totalSales.toLocaleString()} 
        />
        <StatCard 
          icon={<AlertTriangle className="text-maroon" size={20} />} 
          label="Out of Stock" 
          value={stats.outOfStock} 
        />
      </div>

      {/* 4. CONTENT GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 relative z-10">
        {/* STOCK PREVIEW */}
        <div className="bg-white p-6 rounded shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-4 border-b pb-2">
            <h3 className="font-bold text-maroon uppercase text-xs tracking-widest flex items-center">
              <Box size={16} className="mr-2"/> Stock Management
            </h3>
            <button 
              onClick={() => navigate('/admin/stock')}
              className="text-gold font-bold text-[10px] flex items-center hover:text-maroon transition uppercase"
            >
              View More <ArrowRight size={12} className="ml-1"/>
            </button>
          </div>
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-gray-400 uppercase text-[10px] border-b">
                <th className="pb-2">Saree</th>
                <th className="pb-2 text-right">Price</th>
                <th className="pb-2 text-right">Stock</th>
              </tr>
            </thead>
            <tbody>
              {recentProducts.map(p => (
                <tr key={p._id} className="border-b last:border-0 hover:bg-gray-50 transition">
                  <td className="py-3 flex items-center">
                    <img src={p.images?.[0] || "/placeholder.png"} className="w-8 h-10 object-cover mr-3 border rounded-sm" alt=""/>
                    <span className="font-semibold text-gray-700">{p.name}</span>
                  </td>
                  <td className="text-right text-gray-600">₹{p.price}</td>
                  <td className={`text-right font-bold ${p.stock <= 5 ? "text-red-600" : "text-green-600"}`}>{p.stock}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ORDERS PREVIEW */}
        <div className="bg-white p-6 rounded shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-4 border-b pb-2">
            <h3 className="font-bold text-maroon uppercase text-xs tracking-widest flex items-center">
              <ShoppingBag size={16} className="mr-2"/> Latest Orders
            </h3>
            <button 
              onClick={() => navigate('/admin/orders')}
              className="text-gold font-bold text-[10px] flex items-center hover:text-maroon transition uppercase"
            >
              View More <ArrowRight size={12} className="ml-1"/>
            </button>
          </div>
          <div className="space-y-3">
            {recentOrders.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-400 italic text-xs">No orders recorded yet.</p>
              </div>
            ) : (
              recentOrders.map(order => (
                <div key={order._id} className="flex justify-between items-center bg-gray-50 p-4 rounded border-l-2 border-maroon hover:bg-white hover:shadow-sm transition">
                  <div>
                    <p className="font-bold text-xs text-gray-800">{order.customer?.name}</p>
                    <p className="text-[10px] text-gray-400 font-mono mt-1">{order.orderId || order._id}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-maroon text-sm">₹{order.total || order.billing?.total || 0}</p>
                    <span className="text-[9px] font-bold uppercase tracking-tighter text-gray-500">{order.status}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* BACKGROUND WATERMARK */}
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] pointer-events-none">
         <img src="/logo.png" alt="" className="w-[800px]" />
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="bg-white p-6 rounded shadow-sm border border-gray-100 flex items-center group hover:border-maroon transition-all duration-300">
    <div className="bg-[#f5f2e8] p-4 rounded-full mr-5 group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <div>
      <h4 className="text-gray-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-1">{label}</h4>
      <p className="text-2xl font-bold text-gray-800 tracking-tight">{value}</p>
    </div>
  </div>
);

export default AdminDashboard;